import type { ReactNode } from "react"
import type { BoxProps, ButtonProps } from "@chakra-ui/react"
import { Box, Button, Flex, Heading, Icon, Image, Text } from "@chakra-ui/react"
import type { IconType } from "react-icons"
import { FaArrowRight, FaHeart } from "react-icons/fa"
import { CharacterAvatarPreview } from "../features/character/CharacterAvatarPreview"
import { SIDEWAYS_MQ } from "./mediaQueries"
import "../fonts/fredoka.css"

// Shared pieces of the welcome and start screens (client testing report,
// items 5 and 6, built to the client's mockups): the bedroom picture fills
// the viewport, a cream card with a soft pink border holds the content, the
// logo sits over or inside the card's top, round glossy icons mark the
// tiles, and small static decorations (react-icons glyphs and CSS bars)
// are scattered as on the mockups. Plain CSS throughout: no blur,
// backdrop-filter or animation, because the target is a low-end tablet.
//
// Colours are literal hexes sampled from the mockups
// (design-refs/checklist.md on the developer's machine), not theme tokens:
// the theme's pink scale is hot pink, the mockups are raspberry and cream.

// The two-column layouts switch on orientation, not a Chakra breakpoint, so
// the tablet gets one column at 800x1280 and two at 1280x800. 64em (1024px)
// keeps 960x600 in one column too: its half-columns are too narrow for the
// start screen's four tiles, and a scrolling column beats a clipped one.
export const LANDSCAPE_MQ =
  "@media (orientation: landscape) and (min-width: 64em)"
// Short landscape screens (960x600) give up 12px of bottom padding; the
// fixed version tag is 30px tall at most.
const SHORT_MQ = "@media (max-height: 40em)"
// Phones: below Chakra md (768px), which every tablet size is at or above,
// so the phone sizes never touch the tablet layout. The card's fixed sizes
// (border, corners, padding, buttons) scale down here; the pages scale
// their own tiles, logos and decorations with { base, md } props.
export const PHONE_MQ = "@media (max-width: 47.99em)"
// A phone held sideways (SIDEWAYS_MQ, 32em tall or less) gets the phone
// card with a small logo and the screen padding down to 6px, and each page
// its own two-column layout; it is written after PHONE_MQ and LANDSCAPE_MQ
// in every sx so it wins where two of them match.

// Fredoka (SIL OFL 1.1, src/fonts/) for display text only: headings, pill
// labels, buttons and the banner. Body text stays on the system font, as on
// the mockups. Roboto is what the tablets have.
export const DISPLAY_FONT = "'Fredoka', 'Segoe UI', Roboto, sans-serif"

// Inks sampled from the mockups.
export const INK = {
  heading: "#a52c50", // raspberry headings and links
  body: "#3b2f2a", // warm brown-black body text
  plum: "#3d2940", // "You will make choices about:"
  violet: "#6b3790", // "SAFE SPACE"
  maroon: "#5a2226", // "MEET YOUR CHARACTER"
  wine: "#5a1a22", // text on the salmon pills
  plumDark: "#4a1650", // text on the orchid pill
}

// Encoded with the sharp settings in CLAUDE.md (Illustrated screens) from
// masters that are not in the repo; the art is awaiting the client's
// sign-off. The orientation picks the picture, so a tablet decodes only one
// of the two.
const BG_LANDSCAPE = "/screens/bg-landscape.webp"
const BG_PORTRAIT = "/screens/bg-portrait.webp"

export const IllustratedScreen = ({ children }: { children: ReactNode }) => (
  <Box
    minH="100vh"
    position="relative"
    display="flex"
    alignItems="center"
    justifyContent="center"
    px={{ base: 2, md: 4 }}
    // Clear of the fixed corner controls in App.tsx: the options menu (top
    // left), the music button (top right) and the version tag (bottom right).
    pt="44px"
    pb="40px"
    sx={{
      "@supports (height: 100dvh)": { minHeight: "100dvh" },
      [SHORT_MQ]: { paddingBottom: "28px" },
      [PHONE_MQ]: { alignItems: "flex-start" },
      [SIDEWAYS_MQ]: {
        paddingTop: "6px",
        paddingBottom: "6px",
        alignItems: "center",
      },
    }}
    bg="#e9d5ff"
  >
    <Box
      aria-hidden
      position="fixed"
      inset={0}
      zIndex={0}
      pointerEvents="none"
      bgImage={`url(${BG_LANDSCAPE})`}
      bgSize="cover"
      bgPosition="center"
      bgRepeat="no-repeat"
      sx={{
        "@media (orientation: portrait)": {
          backgroundImage: `url(${BG_PORTRAIT})`,
        },
      }}
    />
    <Box
      position="relative"
      zIndex={1}
      w="100%"
      display="flex"
      justifyContent="center"
    >
      {children}
    </Box>
  </Box>
)

interface Orientations {
  portrait: string
  landscape: string
  /** A phone held sideways; the landscape value if left out. */
  sideways?: string
}

interface IllustratedCardProps {
  logoSrc: string
  logoAlt: string
  /** Intrinsic pixel size of the logo file, so the layout does not shift. */
  logoWidth: number
  logoHeight: number
  /** CSS width of the logo in portrait and its CSS height in landscape. */
  logoSize: Orientations
  /**
   * "overlap": the logo hangs over the card's top edge by `logoOverlap`
   * (the welcome screen, ribbon across the edge). "inside": the logo sits
   * inside the card's top (the start screen).
   */
  logoPlacement: "overlap" | "inside"
  logoOverlap?: Orientations
  /** Card width per orientation. */
  maxW: Orientations
  /** The logo is the page's h1 (the start screen has no other title). */
  logoAsHeading?: boolean
  /** Absolutely positioned decorations, placed relative to the card. */
  decor?: ReactNode
  /** Absolutely positioned decorations, placed relative to the logo. */
  logoDecor?: ReactNode
  children: ReactNode
}

export const IllustratedCard = ({
  logoSrc,
  logoAlt,
  logoWidth,
  logoHeight,
  logoSize,
  logoPlacement,
  logoOverlap = { portrait: "0px", landscape: "0px" },
  maxW,
  logoAsHeading = false,
  decor,
  logoDecor,
  children,
}: IllustratedCardProps) => {
  const img = (
    <Image
      src={logoSrc}
      alt={logoAlt}
      htmlWidth={logoWidth}
      htmlHeight={logoHeight}
      display="block"
      objectFit="contain"
      loading="eager"
      sx={{
        width: logoSize.portrait,
        height: "auto",
        maxWidth: "100%",
        // A percentage width inside the stretched h1 needs its own centring.
        marginInline: "auto",
        [LANDSCAPE_MQ]: { width: "auto", height: logoSize.landscape },
        [SIDEWAYS_MQ]: {
          width: "auto",
          height: logoSize.sideways ?? logoSize.landscape,
        },
      }}
    />
  )
  const logo = (
    <Box position="relative" display="inline-block" maxW="100%">
      {logoAsHeading ? (
        <Heading as="h1" m={0} lineHeight={0}>
          {img}
        </Heading>
      ) : (
        img
      )}
      {logoDecor}
    </Box>
  )
  const overlap = logoPlacement === "overlap"
  const sidewaysOverlap = logoOverlap.sideways ?? logoOverlap.landscape
  return (
    <Flex
      direction="column"
      align="center"
      w="100%"
      sx={{
        maxWidth: maxW.portrait,
        [LANDSCAPE_MQ]: { maxWidth: maxW.landscape },
        [SIDEWAYS_MQ]: { maxWidth: maxW.sideways ?? maxW.landscape },
      }}
    >
      {overlap && (
        <Box
          position="relative"
          zIndex={2}
          sx={{
            // calc(-1 * …): a bare minus before min() is not valid CSS.
            marginBottom: `calc(-1 * (${logoOverlap.portrait}))`,
            [LANDSCAPE_MQ]: {
              marginBottom: `calc(-1 * (${logoOverlap.landscape}))`,
            },
            [SIDEWAYS_MQ]: {
              marginBottom: `calc(-1 * (${sidewaysOverlap}))`,
            },
          }}
        >
          {logo}
        </Box>
      )}
      <Box
        position="relative"
        w="100%"
        bgGradient="linear(to-b, #fdf1e1, #fbe3d3)"
        border="7px solid #f0c2e6"
        borderBottomWidth="10px"
        borderBottomColor="#d98fc4"
        borderRadius="88px"
        // Cream inner ring, the mockup's short dark shadow under the card,
        // and the soft pink glow the design asks for.
        boxShadow="inset 0 0 0 3px #fff3ea, 0 0 0 8px rgba(249, 168, 212, 0.32), 0 10px 16px -4px rgba(96, 32, 64, 0.5)"
        px={{ base: 3, md: 5 }}
        pb={{ base: 4, md: 5 }}
        sx={{
          paddingTop: overlap ? `calc(${logoOverlap.portrait} + 12px)` : "14px",
          [LANDSCAPE_MQ]: {
            paddingTop: overlap
              ? `calc(${logoOverlap.landscape} + 12px)`
              : "14px",
          },
          // Smaller corners and border on a phone, so they do not eat into
          // the content; the cream ring and glow shrink with them.
          [PHONE_MQ]: {
            borderRadius: "36px",
            borderWidth: "4px",
            borderBottomWidth: "6px",
            boxShadow:
              "inset 0 0 0 2px #fff3ea, 0 0 0 5px rgba(249, 168, 212, 0.32), 0 8px 12px -4px rgba(96, 32, 64, 0.5)",
          },
          [SIDEWAYS_MQ]: {
            paddingTop: overlap ? `calc(${sidewaysOverlap} + 6px)` : "8px",
            paddingInline: "16px",
            paddingBottom: "10px",
            borderRadius: "32px",
            borderWidth: "4px",
            borderBottomWidth: "6px",
            boxShadow:
              "inset 0 0 0 2px #fff3ea, 0 0 0 5px rgba(249, 168, 212, 0.32), 0 8px 12px -4px rgba(96, 32, 64, 0.5)",
          },
        }}
      >
        {decor}
        {!overlap && (
          <Flex
            justify="center"
            mb={3}
            sx={{ [SIDEWAYS_MQ]: { marginBottom: "4px" } }}
          >
            {logo}
          </Flex>
        )}
        {children}
      </Box>
    </Flex>
  )
}

// ---------------------------------------------------------------------------
// Decorations: static, hidden from assistive technology, never over text.

interface DecoProps extends BoxProps {
  icon: IconType
  size: string
  color: string
  /** Sticker look: a white outline around the glyph. */
  outline?: string
  /** Its size on a phone held sideways, if different. */
  sidewaysSize?: string
}

export const Deco = ({
  icon,
  size,
  color,
  outline,
  sidewaysSize,
  ...pos
}: DecoProps) => (
  <Box
    as="span"
    aria-hidden
    pointerEvents="none"
    position="absolute"
    display="block"
    lineHeight={0}
    {...pos}
  >
    <Icon
      as={icon}
      boxSize={size}
      color={color}
      // The outline is painted outside the glyph, which fills its viewBox.
      overflow="visible"
      sx={{
        ...(outline
          ? { path: { stroke: outline, strokeWidth: 40, paintOrder: "stroke" } }
          : undefined),
        ...(sidewaysSize
          ? { [SIDEWAYS_MQ]: { width: sidewaysSize, height: sidewaysSize } }
          : undefined),
      }}
    />
  </Box>
)

// Three short strokes fanning out, as the mockups draw beside pill labels
// and headings. Plain CSS bars; `flip` mirrors it for the left side.
export const Burst = ({
  color,
  size = "24px",
  flip = false,
  ...pos
}: BoxProps & { color: string; size?: string; flip?: boolean }) => (
  <Box
    as="span"
    aria-hidden
    pointerEvents="none"
    position="absolute"
    display="block"
    w={size}
    h={size}
    transform={flip ? "scaleX(-1)" : undefined}
    {...pos}
  >
    {/* The strokes radiate from the box's left edge but start 30% in, so
        they never meet: three separate strokes, not an arrowhead. */}
    {[-30, 0, 30].map(deg => (
      <Box
        key={deg}
        as="span"
        position="absolute"
        left="30%"
        top="50%"
        w="55%"
        h="2px"
        borderRadius="full"
        bg={color}
        transformOrigin="-54.5% 50%"
        transform={`translateY(-50%) rotate(${deg}deg)`}
      />
    ))}
  </Box>
)

// ---------------------------------------------------------------------------

// Round glossy icon: flat base colour, a soft highlight at the top left, a
// faint darker rim and a lighter ring, as on the mockup's stickers.
export const IconCircle = ({
  icon,
  bg,
  size = "44px",
  iconSize = "20px",
  sidewaysSize,
  sidewaysIconSize,
}: {
  icon: IconType
  bg: string
  size?: string
  iconSize?: string
  /** Sizes on a phone held sideways, if different. */
  sidewaysSize?: string
  sidewaysIconSize?: string
}) => (
  <Flex
    align="center"
    justify="center"
    w={size}
    h={size}
    borderRadius="full"
    bg={bg}
    bgGradient="radial(circle at 32% 28%, rgba(255,255,255,0.55), rgba(255,255,255,0) 48%)"
    color="white"
    flexShrink={0}
    boxShadow="0 0 0 2.5px rgba(255,255,255,0.85), inset 0 -3px 0 rgba(0,0,0,0.12)"
    sx={
      sidewaysSize
        ? { [SIDEWAYS_MQ]: { width: sidewaysSize, height: sidewaysSize } }
        : undefined
    }
  >
    <Icon
      as={icon}
      boxSize={iconSize}
      aria-hidden
      sx={
        sidewaysIconSize
          ? {
              [SIDEWAYS_MQ]: {
                width: sidewaysIconSize,
                height: sidewaysIconSize,
              },
            }
          : undefined
      }
    />
  </Flex>
)

export const TopicTile = ({
  icon,
  bg,
  label,
  layout = "row",
  tileBg = "#fdf3e3",
  tileBorder = "#e8d2c0",
  circleSize = "56px",
  iconSize = "30px",
  labelSize = "lg",
  sparkles,
}: {
  icon: IconType
  bg: string
  label: string
  layout?: "row" | "column"
  tileBg?: string
  tileBorder?: string
  circleSize?: string
  iconSize?: string
  labelSize?: string
  /** Two tiny marks beside the circle in this colour. */
  sparkles?: ReactNode
}) => (
  <Flex
    direction={layout}
    align="center"
    justify="flex-start"
    textAlign={layout === "column" ? "center" : "left"}
    gap={layout === "column" ? 2 : 4}
    bg={tileBg}
    border="2px solid"
    borderColor={tileBorder}
    borderRadius={layout === "column" ? "16px" : "14px"}
    p={layout === "column" ? "8px 6px 7px" : "9px 12px"}
    minH={layout === "column" ? "94px" : "74px"}
    minW={0}
    // A phone held sideways: a shallower tile with a smaller circle and a
    // 14px label, in both layouts.
    sx={{
      [SIDEWAYS_MQ]:
        layout === "column"
          ? { minHeight: 0, padding: "5px 3px 4px", gap: "3px" }
          : {
              minHeight: "42px",
              padding: "3px 10px",
              gap: "10px",
              borderRadius: "12px",
            },
    }}
  >
    <Box position="relative" flexShrink={0}>
      <IconCircle
        icon={icon}
        bg={bg}
        size={circleSize}
        iconSize={iconSize}
        sidewaysSize={layout === "column" ? "36px" : "38px"}
        sidewaysIconSize="20px"
      />
      {sparkles}
    </Box>
    <Text
      fontSize={labelSize}
      fontWeight={layout === "column" ? "bold" : "semibold"}
      color={layout === "column" ? "#46332c" : INK.body}
      lineHeight="1.15"
      sx={{ [SIDEWAYS_MQ]: { fontSize: "14px" } }}
    >
      {label}
    </Text>
  </Flex>
)

export const HeartDivider = () => (
  <Flex align="center" gap={1} w="100%" px="6%" aria-hidden>
    <Box flex="1" h="1.5px" bg="#f3cbd3" borderRadius="full" />
    <Icon as={FaHeart} color="#ec668a" boxSize="22px" mx={1} />
    <Box flex="1" h="1.5px" bg="#f3cbd3" borderRadius="full" />
  </Flex>
)

// Uppercase pill label above a section ("HOW TO PLAY"), with the mockup's
// three-stroke bursts either side. The transform is CSS, so the DOM keeps
// the wording the client approved.
export const SectionLabel = ({
  children,
  scheme = "pink",
  fontSize = "20px",
  px = "26px",
  sidewaysFontSize = "16px",
  sidewaysPx = "18px",
}: {
  children: ReactNode
  scheme?: "pink" | "purple"
  fontSize?: string
  px?: string
  /** Size and side padding on a phone held sideways. */
  sidewaysFontSize?: string
  sidewaysPx?: string
}) => {
  const pink = scheme === "pink"
  return (
    <Box position="relative" alignSelf="center">
      <Text
        as="h2"
        fontFamily={DISPLAY_FONT}
        fontSize={fontSize}
        fontWeight={700}
        lineHeight="1.2"
        // Phone cards are narrow: the label scales with the width and may
        // wrap; its bursts move in with it.
        sx={{
          [PHONE_MQ]: {
            fontSize: `clamp(13px, 4.2vw, ${fontSize})`,
            whiteSpace: "normal",
            paddingInline: "14px",
            paddingBlock: "4px",
          },
          [SIDEWAYS_MQ]: {
            fontSize: sidewaysFontSize,
            whiteSpace: "nowrap",
            paddingInline: sidewaysPx,
            paddingBlock: "3px",
          },
        }}
        textTransform="uppercase"
        color={pink ? INK.wine : INK.plumDark}
        bgGradient={
          pink
            ? "linear(to-b, #fcc3bd, #f5b3ad)"
            : "linear(to-b, #e5a9e7, #d995dc)"
        }
        px={px}
        py="5px"
        borderRadius="full"
        textAlign="center"
        whiteSpace="nowrap"
      >
        {children}
      </Text>
      <Burst
        color={pink ? "#d2607a" : "#9b5fb8"}
        flip
        right="100%"
        top="50%"
        transform="translateY(-50%) scaleX(-1)"
        mr="6px"
      />
      <Burst
        color={pink ? "#d2607a" : "#9b5fb8"}
        left="100%"
        top="50%"
        transform="translateY(-50%)"
        ml="6px"
      />
    </Box>
  )
}

// Button faces sampled from the mockups; the lip is the darker bottom edge
// that makes them look raised.
const BUTTONS = {
  coral: {
    base: "linear(to-b, #f4537f, #ec4d7a)",
    hover: "linear(to-b, #ee4a76, #e3426f)",
    lip: "#c02a58",
  },
  // The mockup's face runs #ee226b to #f0276f (white on it 4.1:1); the whole
  // face is one step deeper so the 13px subtitle reaches 4.5:1 wherever it
  // sits on the gradient (4.6:1 at the top stop, 5.1:1 at the bottom).
  pink: {
    base: "linear(to-b, #e8186a, #d60f58)",
    hover: "linear(to-b, #dc1563, #cb0c52)",
    lip: "#b31647",
  },
  purple: {
    base: "linear(to-b, #7a3dc4, #6a2bba)",
    hover: "linear(to-b, #7034b8, #6226ad)",
    lip: "#592589",
  },
}

interface PillButtonProps extends ButtonProps {
  scheme: keyof typeof BUTTONS
  /** Small second line under the label. */
  subtitle?: string
  /** CSS-only capitals for the label (the DOM keeps the approved wording). */
  uppercase?: boolean
  /** A sticker glyph on the button's edge. */
  sticker?: ReactNode
}

// The label stays first in the DOM: scripts/perf/cdp-run.mjs finds these
// buttons by the start of their textContent.
export const PillButton = ({
  scheme,
  subtitle,
  uppercase = false,
  sticker,
  children,
  ...rest
}: PillButtonProps) => {
  const look = BUTTONS[scheme]
  return (
    <Button
      size="lg"
      h={{ base: "60px", md: "72px" }}
      // The two-line game buttons are narrower: keep their capitals clear
      // of the sticker on the corner.
      px={subtitle ? 5 : 8}
      position="relative"
      overflow="visible"
      borderRadius="full"
      bgGradient={look.base}
      color="white"
      fontFamily={DISPLAY_FONT}
      fontSize={
        subtitle ? { base: "16px", md: "18px" } : { base: "20px", md: "24px" }
      }
      fontWeight={700}
      letterSpacing={uppercase ? "0.02em" : "normal"}
      border={subtitle ? "2px solid rgba(255,255,255,0.55)" : "none"}
      boxShadow={`0 6px 0 ${look.lip}, 0 10px 12px rgba(110, 30, 50, 0.3)`}
      mb="6px"
      whiteSpace="normal"
      iconSpacing={subtitle ? 3 : 5}
      _hover={{ bgGradient: look.hover, transform: "translateY(-1px)" }}
      _active={{
        transform: "translateY(4px)",
        boxShadow: `0 2px 0 ${look.lip}`,
      }}
      transition="transform 0.1s, box-shadow 0.1s"
      rightIcon={<Icon as={FaArrowRight} boxSize="22px" />}
      {...rest}
    >
      {/* A span, not a div: a button may only hold phrasing content. */}
      <Flex as="span" direction="column" align="center" lineHeight="1.1">
        <Text
          as="span"
          textTransform={uppercase ? "uppercase" : "none"}
          whiteSpace="nowrap"
        >
          {children}
        </Text>
        {subtitle && (
          <Text
            as="span"
            fontFamily="body"
            fontSize="13px"
            fontWeight="semibold"
            letterSpacing="normal"
            mt="2px"
          >
            {subtitle}
          </Text>
        )}
      </Flex>
      {sticker}
    </Button>
  )
}

// ---------------------------------------------------------------------------
// "Meet your character", with the player's real avatar. Both screens use it,
// so the wording lives in one place; the two mockups lay it out differently.

const FIRST_PARAGRAPH =
  "She is you in this story. You choose how she looks — her skin tone, hair, eyebrows, glasses, outfit and shoes."

const SecondParagraph = ({ linkColor }: { linkColor: string }) => (
  <>
    Change her look any time: tap her picture beside the story, or open the menu
    at the top left and choose{" "}
    <Text as="span" fontWeight="bold" color={linkColor}>
      Edit Character
    </Text>
    .
  </>
)

/** Welcome screen: full figure on the left, title and two paragraphs. */
export const MeetYourCharacterPanel = ({
  titleMark,
  ...rest
}: BoxProps & { titleMark?: ReactNode }) => (
  <Box
    bg="#fdeade"
    border="2px solid #eccbc0"
    borderRadius="17px"
    px={{ base: 4, md: 5 }}
    py={3}
    {...rest}
  >
    <Flex
      direction={{ base: "column", sm: "row" }}
      align="center"
      gap={{ base: 3, sm: 6 }}
      sx={{ [SIDEWAYS_MQ]: { gap: "12px" } }}
    >
      {/* The art has transparent padding above the head and below the
          feet; pulling it out keeps the figure at ~90% of the panel, as on
          the mockup. Not in the stacked phone layout, where it would pull
          the text up into the figure. */}
      <Box
        my={{ base: 0, sm: "-20px" }}
        sx={{ [SIDEWAYS_MQ]: { marginBlock: 0 } }}
      >
        <CharacterAvatarPreview
          height="240px"
          phoneHeight="180px"
          sidewaysHeight="150px"
        />
      </Box>
      <Flex
        direction="column"
        align={{ base: "center", sm: "flex-start" }}
        textAlign={{ base: "center", sm: "left" }}
        gap={2}
        sx={{ [SIDEWAYS_MQ]: { gap: "4px" } }}
      >
        <Box position="relative" display="inline-block">
          <Text
            as="h2"
            fontFamily={DISPLAY_FONT}
            fontSize="24px"
            fontWeight={600}
            lineHeight="1.2"
            color={INK.heading}
            sx={{ [SIDEWAYS_MQ]: { fontSize: "19px" } }}
          >
            Meet your character
          </Text>
          {titleMark}
        </Box>
        <Text
          fontSize="md"
          color={INK.body}
          lineHeight="1.5"
          sx={{ [SIDEWAYS_MQ]: { fontSize: "14px", lineHeight: "1.4" } }}
        >
          {FIRST_PARAGRAPH}
        </Text>
        <Text
          fontSize="md"
          color={INK.body}
          lineHeight="1.5"
          sx={{ [SIDEWAYS_MQ]: { fontSize: "14px", lineHeight: "1.4" } }}
        >
          <SecondParagraph linkColor={INK.heading} />
        </Text>
      </Flex>
    </Flex>
  </Box>
)
